// Generate or retrieve session ID for guest users
export const getSessionId = () => {
  let sessionId = localStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

/**
 * 回退策略枚举
 */
export const FallbackStrategy = {
  // 显示默认语言内容（中文）- 向后兼容的默认行为
  SHOW_DEFAULT: 'show_default',
  // 返回 null，让组件决定如何处理（可以隐藏该字段）
  RETURN_NULL: 'return_null',
  // 显示占位符文本
  SHOW_PLACEHOLDER: 'show_placeholder'
};

// 需要自动翻译的语言列表（非中英文）
const AUTO_TRANSLATE_LANGUAGES = ['ru', 'uk', 'fr', 'es', 'pt', 'ja', 'ko', 'ar', 'uz'];

/**
 * 获取本地化字段值，带有优雅的回退处理
 * 
 * 对于中英文：直接返回对应字段
 * 对于其他语言：优先返回英文或中文内容（前端会异步翻译）
 * 
 * @param {Object} item - 数据库对象（产品、作品等）
 * @param {string} field - 字段名（如 'name', 'description'）
 * @param {string} lang - 目标语言代码（'zh', 'en', 'ru'等）
 * @param {Object} options - 配置选项
 * @param {string} options.fallback - 回退策略，默认 'show_default'
 * @param {string} options.placeholder - 自定义占位符文本
 * @returns {string|null} 本地化后的字段值
 * 
 * @example
 * // 默认行为（向后兼容）：如果没有英文翻译，显示中文
 * getLocalizedField(product, 'name', 'en')
 * 
 * @example
 * // 返回 null，让组件可以选择隐藏该字段
 * getLocalizedField(product, 'description', 'en', { fallback: FallbackStrategy.RETURN_NULL })
 * 
 * @example
 * // 显示自定义占位符
 * getLocalizedField(product, 'name', 'ru', { 
 *   fallback: FallbackStrategy.SHOW_PLACEHOLDER, 
 *   placeholder: '[翻译缺失]' 
 * })
 */
export const getLocalizedField = (item, field, lang, options = {}) => {
  if (!item) return '';
  
  const { 
    fallback = FallbackStrategy.SHOW_DEFAULT,
    placeholder = null
  } = options;
  
  // 默认语言是中文，直接返回基础字段
  if (lang === 'zh') {
    return item[field] || '';
  }
  
  // 英文：优先使用英文字段，回退到中文
  if (lang === 'en') {
    return item[`${field}_en`] || item[field] || '';
  }
  
  // 其他语言（需要自动翻译的语言）
  // 优先返回英文内容（翻译效果更好），回退到中文
  const langField = `${field}_${lang}`;
  const translatedValue = item[langField];
  
  // 如果有现成的翻译，直接返回
  if (translatedValue && translatedValue.trim()) {
    return translatedValue;
  }
  
  // 没有翻译时，根据回退策略处理
  // 对于自动翻译语言，返回英文或中文内容作为翻译源
  switch (fallback) {
    case FallbackStrategy.RETURN_NULL:
      // 返回 null，让调用者决定如何处理
      return null;
      
    case FallbackStrategy.SHOW_PLACEHOLDER:
      // 显示占位符
      const defaultPlaceholders = {
        en: '[Translation not available]',
        ru: '[Перевод недоступен]',
        uk: '[Переклад недоступний]',
        fr: '[Traduction non disponible]',
        es: '[Traducción no disponible]',
        pt: '[Tradução não disponível]',
        ja: '[翻訳がありません]',
        ko: '[번역 불가]',
        ar: '[الترجمة غير متوفرة]',
        uz: '[Tarjima mavjud emas]'
      };
      return placeholder || defaultPlaceholders[lang] || '[--]';
      
    case FallbackStrategy.SHOW_DEFAULT:
    default:
      // 默认行为：返回英文或中文内容（前端会异步翻译）
      return item[`${field}_en`] || item[field] || '';
  }
};

/**
 * 检查某个字段是否有指定语言的翻译
 * 
 * @param {Object} item - 数据库对象
 * @param {string} field - 字段名
 * @param {string} lang - 语言代码
 * @returns {boolean} 是否有翻译
 */
export const hasTranslation = (item, field, lang) => {
  if (!item || lang === 'zh') return true;
  const langField = `${field}_${lang}`;
  const value = item[langField];
  return !!(value && value.trim());
};

/**
 * 获取本地化字段，如果没有翻译则返回带标记的默认值
 * 适用于需要明确标识未翻译内容的场景
 * 
 * @param {Object} item - 数据库对象
 * @param {string} field - 字段名
 * @param {string} lang - 语言代码
 * @param {string} marker - 标记符号，默认 '*'
 * @returns {Object} { value: string, isTranslated: boolean }
 */
export const getLocalizedFieldWithStatus = (item, field, lang, marker = '*') => {
  if (!item) return { value: '', isTranslated: true };
  
  if (lang === 'zh') {
    return { value: item[field] || '', isTranslated: true };
  }
  
  const langField = `${field}_${lang}`;
  const translatedValue = item[langField];
  
  if (translatedValue && translatedValue.trim()) {
    return { value: translatedValue, isTranslated: true };
  }
  
  // 没有翻译，返回默认值并标记
  return { 
    value: item[field] || '', 
    isTranslated: false 
  };
};

// Format date based on locale
export const formatDate = (date, lang = 'zh') => {
  const localeMap = {
    zh: 'zh-CN',
    en: 'en-US',
    ru: 'ru-RU',
    uk: 'uk-UA',
    fr: 'fr-FR',
    es: 'es-ES',
    pt: 'pt-BR',
    ja: 'ja-JP',
    ko: 'ko-KR',
    ar: 'ar-SA',
    uz: 'uz-UZ'
  };
  return new Date(date).toLocaleString(localeMap[lang] || 'zh-CN');
};

// Format currency
export const formatCurrency = (amount, currency = 'CNY') => {
  return `¥${amount.toFixed(2)}`;
};

// Truncate text
export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

/**
 * Get the full URL for an image.
 * Handles both relative URLs (from uploaded files) and absolute URLs (external).
 * 
 * @param {string} url - The image URL (can be relative or absolute)
 * @param {string} fallback - Optional fallback URL if the input is empty
 * @returns {string} The full image URL
 * 
 * Examples:
 * - /api/uploads/images/abc.jpg -> https://current-domain.com/api/uploads/images/abc.jpg
 * - https://images.unsplash.com/photo-123 -> https://images.unsplash.com/photo-123 (unchanged)
 * - https://old-domain.com/api/uploads/images/abc.jpg -> https://current-domain.com/api/uploads/images/abc.jpg
 */
export const getImageUrl = (url, fallback = '') => {
  if (!url) return fallback;
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Fix old domain URLs — this is the critical migration path
    if (url.includes('organic-food-hub')) {
      // Extract /api/... path and rebuild with current backend
      const apiMatch = url.match(/(\/api\/.+)$/);
      if (apiMatch) {
        return `${backendUrl}${apiMatch[1]}`;
      }
    }
    
    // Fix Alibaba Cloud OSS direct URLs → proxy through backend
    if (url.includes('aliyuncs.com')) {
      const match = url.match(/aliyuncs\.com\/(.+?)(\?|$)/);
      if (match) {
        return `${backendUrl}/api/oss-proxy/${match[1]}`;
      }
    }
    
    // Any other full URL (unsplash, pexels, etc.) — return as-is
    return url;
  }
  
  // Relative URL — prepend backend URL
  if (url.startsWith('/')) {
    return `${backendUrl}${url}`;
  }
  
  return url || fallback;
};

/**
 * Default avatar URL for users without a profile picture
 */
export const DEFAULT_AVATAR = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop';

/**
 * Get user avatar URL with fallback
 * @param {string} avatar - User's avatar URL
 * @returns {string} Avatar URL or default avatar
 */
export const getUserAvatar = (avatar) => {
  return getImageUrl(avatar, DEFAULT_AVATAR);
};

/**
 * Get product/recipe image URL with fallback
 * @param {string} imageUrl - Image URL
 * @returns {string} Image URL or placeholder
 */
export const getProductImage = (imageUrl) => {
  const placeholder = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop';
  return getImageUrl(imageUrl, placeholder);
};


/**
 * Handle broken image fallback
 * @param {Event} e - Image error event 
 */
export const handleImageError = (e) => {
  e.target.onerror = null;
  e.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" fill="%23f5f0eb"><rect width="400" height="300"/><text x="200" y="140" text-anchor="middle" fill="%23c4a882" font-size="16" font-family="serif">Image Unavailable</text><text x="200" y="170" text-anchor="middle" fill="%23d4c4a8" font-size="40">&#x1f3a8;</text></svg>');
};
